import { create } from 'zustand';
import { SIGNALR_BRIDGE_MESSAGES } from '@/types/signalRBridge';
import { SignalRConnectionState, SignalRStore } from '@/types/signalr';
import { SignalRBridge } from '@/utils/signalRBridge';

export const useSignalRStore = create<SignalRStore>((set, get) => {
  let bridge: SignalRBridge | null = null;

  return {
    // Initial state
    bridge: null,
    connectionState: SignalRConnectionState.DISCONNECTED,
    isConnecting: false,
    isConnected: false,
    error: null,
    url: null,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5,
    groupName: null,

    // Helper functions for state checks
    isConnectionActive: () => {
      const state = get();
      return (
        state.isConnected &&
        state.connectionState === SignalRConnectionState.CONNECTED
      );
    },

    canReconnect: () => {
      const state = get();
      return state.reconnectAttempts < state.maxReconnectAttempts;
    },

    // Actions
    actions: {
      connect: async (url: string, location: string, groupName: string) => {
        const state = get();

        // Prevent multiple concurrent connections
        if (state.isConnecting || state.isConnected) {
          return;
        }

        try {
          set({
            isConnecting: true,
            connectionState: SignalRConnectionState.CONNECTING,
            error: null,
            url,
            groupName,
          });

          // Create bridge instead of direct connection
          bridge = new SignalRBridge();

          bridge.onConnectionStateChanged((state) => {
            let connectionState = SignalRConnectionState.DISCONNECTED;
            let isConnecting = false;

            if (state === SIGNALR_BRIDGE_MESSAGES.CONNECTED) {
              connectionState = SignalRConnectionState.CONNECTED;
            } else if (state === SIGNALR_BRIDGE_MESSAGES.RECONNECTING) {
              connectionState = SignalRConnectionState.RECONNECTING;
              isConnecting = false;
            }

            set({
              connectionState,
              isConnected: connectionState === SignalRConnectionState.CONNECTED,
              isConnecting, // Remove the problematic comparison
            });
          });

          bridge.connect(url, location, groupName);

          console.log('SignalR bridge connected successfully');

          set({
            bridge,
            connectionState: SignalRConnectionState.CONNECTED,
            isConnected: true,
            isConnecting: false,
            error: null,
            reconnectAttempts: 0,
          });
        } catch (error) {
          console.error('SignalR connection failed:', error);
          const errorMessage =
            error instanceof Error ? error.message : 'Connection failed';

          set({
            bridge: null,
            connectionState: SignalRConnectionState.DISCONNECTED,
            isConnected: false,
            isConnecting: false,
            error: errorMessage,
          });

          throw error;
        }
      },

      disconnect: async () => {
        try {
          set({
            connectionState: SignalRConnectionState.DISCONNECTING,
            isConnecting: false,
          });

          if (bridge) {
            bridge.destroy();
            bridge = null;
          }

          set({
            bridge: null,
            connectionState: SignalRConnectionState.DISCONNECTED,
            isConnected: false,
            isConnecting: false,
            error: null,
            url: null,
            groupName: null,
          });
        } catch (error) {
          console.error('SignalR disconnect failed:', error);
          const errorMessage =
            error instanceof Error ? error.message : 'Disconnect failed';

          set({
            error: errorMessage,
          });

          throw error;
        }
      },

      invokeMethod: async (methodName: string, ...args: unknown[]) => {
        if (!bridge) {
          throw new Error('SignalR connection is not active');
        }

        try {
          await bridge.invoke(methodName, ...args);
        } catch (error) {
          console.error(
            `SignalR invoke method failed for method ${methodName}:`,
            error,
          );
          throw error;
        }
      },

      subscribe: (
        methodName: string,
        callback: (...args: unknown[]) => void,
      ) => {
        if (!bridge) {
          console.warn(
            'Cannot register handler: SignalR connection is not available',
          );
          return;
        }

        try {
          bridge.on(methodName, callback);
          console.log(`Subscribed to SignalR method: ${methodName}`);
        } catch (error) {
          console.error(
            `Failed to subscribe to SignalR method ${methodName}:`,
            error,
          );
        }
      },

      unsubscribe: (methodName: string) => {
        if (!bridge) {
          return;
        }

        try {
          bridge.off(methodName);
          console.log(`Unsubscribed from SignalR method: ${methodName}`);
        } catch (error) {
          console.error(
            `Failed to unsubscribe from SignalR method ${methodName}:`,
            error,
          );
        }
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      resetReconnectAttempts: () => {
        set({ reconnectAttempts: 0 });
      },
    },
  };
});
